import { toast } from '@oy/uidesktop';
import * as Sentry from '@sentry/browser';
import qs from 'query-string';
import { useEffect, useState } from 'react';
import TagManager from 'react-gtm-module';
import { useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { authLogin } from 'src/clients';
import {
  LOGIN_LIMITER_STATUS_MESSAGE,
  LoginLimiterStatus,
  OTP_REQUEST_STATUS_MESSAGE,
  OtpRequestStatus,
} from 'src/constants';
import { useLogout } from 'src/hooks';
import { useLoginStaging } from 'src/hooks/useLoginStaging';
import { getAuth } from 'src/redux/selectors';
import { storage } from 'src/utilities/Storage';
import { dayJs } from 'src/utilities/day';
import { sha256 } from 'src/utilities/hash';
import stringHash from 'string-hash';
import { useAuthTrack } from '../hooks/useAuthTrack';
import { LoginFormData, LoginMessageData } from './components';
import { LOGIN_ERROR_MESSAGES } from './constants';
import useLoginTrack from './hooks/useLoginTrack';
import { MessageTemplateData, MessageType } from './types';

type LoginImplParams = {
  isRelogin?: boolean;
};

export const useLoginImpl = ({ isRelogin = false }: LoginImplParams = {}) => {
  const [message, setMessage] = useState<LoginMessageData | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoginSuperAdmin, setIsLoginSuperAdmin] = useState(false);
  const { trackVerifyEmail, hitLoginPage, setUserId } = useLoginTrack();

  const [searchParams] = useSearchParams();

  const auth = useSelector(getAuth);

  const navigate = useNavigate();
  const [showDevConfig, setShowDevConfig] = useState(false);

  const { trackClickSignUp } = useAuthTrack();
  const { actions } = useLoginStaging();

  const { doLogout } = useLogout();

  const getLoginMessageFromRef = (ref: string): LoginMessageData | null => {
    let messageKey = '';
    switch (ref) {
      case 'account-already-activated':
        messageKey = 'login_refMessage_accountAlreadyActivated';
        break;
      case 'activation-success':
        messageKey = 'login_refMessage_activationSuccess';
        break;
      case 'forgot-password-success':
        messageKey = 'login_refMessage_forgotPasswordSuccess';
        break;
      case 'input-new-password-success':
        messageKey = 'login_refMessage_inputNewPasswordSuccess';
        break;
      case 'update-password-success':
        messageKey = 'login_refMessage_updatePasswordSuccess';
        break;
      default:
        break;
    }
    if (!messageKey) return null;
    return {
      type: 'success',
      template: {
        key: messageKey,
      },
    };
  };

  const getLoginMessageFromError = (
    username: string,
    { reason, message }: any,
    { status, remaining_time }: any,
  ): LoginMessageData | null => {
    const defaultTemplateData: MessageTemplateData = { username };

    const mappedMessage = LOGIN_ERROR_MESSAGES.find(e => e.message === message);
    if (mappedMessage) {
      let type: MessageType = 'error';
      if (mappedMessage.key === 'login_statusMessage_notActivated') {
        type = 'warning';
      }
      return {
        type,
        template: {
          key: mappedMessage.key,
          data: defaultTemplateData,
        },
      };
    }

    if (reason) return { type: 'error', message: reason };

    switch (status) {
      case OtpRequestStatus.Success:
      case OtpRequestStatus.AlreadyRequested:
      case OtpRequestStatus.UserNotFound:
      case OtpRequestStatus.WaitingActivation: {
        const messageKey = OTP_REQUEST_STATUS_MESSAGE[status]?.key ?? '';
        if (!messageKey) break;
        let type: MessageType = 'error';
        if (status === OtpRequestStatus.Success) {
          type = 'success';
        } else if (
          status === OtpRequestStatus.AlreadyRequested ||
          status === OtpRequestStatus.WaitingActivation
        ) {
          type = 'warning';
        }
        return {
          type,
          template: {
            key: messageKey,
            data: {
              ...defaultTemplateData,
              remainingTime: remaining_time,
            },
          },
        };
      }
      case LoginLimiterStatus.SoftLocked:
      case LoginLimiterStatus.HardLocked:
      case LoginLimiterStatus.InCoolDown:
      case LoginLimiterStatus.SoftLockedAndForgotPasswordInCoolDown: {
        const messageKey = LOGIN_LIMITER_STATUS_MESSAGE[status]?.key ?? '';
        if (!messageKey) break;
        return {
          type: 'warning',
          template: {
            key: messageKey,
            data: {
              ...defaultTemplateData,
              remainingTime: remaining_time,
            },
          },
        };
      }
      default:
        break;
    }

    return null;
  };

  useEffect(() => {
    if (isRelogin) return;
    hitLoginPage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRelogin]);

  useEffect(() => {
    const ref = qs.parse(window.location.search).ref;
    const username = qs.parse(window.location.search).username;
    if (isRelogin) return;
    if (!ref || typeof ref !== 'string') return;
    if (!username || typeof username !== 'string') return;
    if (ref === 'activation-success') {
      trackVerifyEmail(username);
      setIsLoginSuperAdmin(true);
    }
    setMessage(getLoginMessageFromRef(ref));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRelogin]);

  const showInputOtp = () => {
    if (isRelogin) {
      actions.showOtpModal(true);
      return;
    }

    navigate(`/otp?${searchParams.toString()}`);
  };
  const submitLogin = async (formData: LoginFormData) => {
    const composeUsername = () => {
      if (formData.isLoginSubAccount) {
        return [formData.username, formData.subAccountUsername].join('~');
      }
      return formData.username;
    };

    const username = composeUsername();

    try {
      storage.remove('app-cache');
      storage.remove('showOtpGuidelines');
      setMessage(null);
      setIsSubmitting(true);

      const trustToken = JSON.parse(storage.get('trustToken', '{}'));

      Sentry.setUser({ username });

      const response = await authLogin({
        username,
        password: sha256(formData.password),
        trustToken: trustToken[username],
      });
      const data = response.data.data;
      storage.set('lastOtpSend', dayJs());

      TagManager.dataLayer({
        dataLayer: {
          event: 'login_success_business',
          user_id: stringHash(username),
        },
      });
      setUserId(username);

      storage.set('triggerUpgradeReminder', true);

      if (data?.token) {
        await actions.setLoginData({
          username,
          password_expired: data?.password_expired ?? false,
          payment_services: data?.payment_services,
          profile_pict: data?.profile_pict,
          session_id: data?.session_id,
          token: data?.token,
          trust_token: data?.trust_token,
          wallet_info: data?.wallet_info,
        });
        await actions.triggerLoginStaging({
          username,
          shouldCheckCreatedTime: !isRelogin,
        });
      } else if (!data?.token && data?.otp_intermediary_token) {
        actions.setOtpData({
          username,
          intermediaryToken: data?.otp_intermediary_token,
          email: data?.user_email,
        });
        showInputOtp();
      } else if (data?.reset_token) {
        const resetToken = data?.reset_token;
        window.location.href = `/update-password/${resetToken}`;
      }
      setIsSubmitting(false);
    } catch (error: any) {
      if (error.response) {
        const data = error.response.data;
        const authOtpUsername = storage.get('auth')?.otp?.username;

        if (
          data.status === OtpRequestStatus.AlreadyRequested &&
          authOtpUsername === username
        ) {
          showInputOtp();
          return;
        }

        setMessage(
          getLoginMessageFromError(
            username,
            error.response.data,
            error.response.data.data || {},
          ),
        );
      } else if (error.request) {
        toast.error('No response from server');
      } else {
        toast.error(error.message);
      }

      setIsSubmitting(false);
    }
  };

  const onClickSignUp = () => {
    trackClickSignUp();
    navigate('/register');
  };

  const onClickLogout = async () => {
    await doLogout({ from: 'pop_up' });
  };

  return {
    actions: { submitLogin, onClickSignUp, onClickLogout, setShowDevConfig },
    message,
    loggedInUsername: auth.userDefault?.username ?? '',
    isSubmitting,
    isShowLoginModal: auth.isSessionExpired && !auth.isShowOtpModal,
    isLoginSuperAdmin,
    showDevConfig,
  };
};
